import {
	doc,
	getDoc,
	setDoc,
} from 'firebase/firestore';
import { db } from './config';

export const saveUserToFirestore = async (user) => {
	if (!user || !user.uid) {
		throw new Error('No user data provided');
	}

	try {
		const userRef = doc(db, 'users', user.uid);
		// Get reliable timezone information
		const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const offsetMinutes = -new Date().getTimezoneOffset();
		const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
			.toString()
			.padStart(2, '0');
		const offsetMins = (Math.abs(offsetMinutes) % 60)
			.toString()
			.padStart(2, '0');
		const offsetSign = offsetMinutes >= 0 ? '+' : '-';
		const userTimezoneOffset = `${offsetSign}${offsetHours}:${offsetMins}`;

		// Track ALL auth providers, not just the current/first one
		const authProviders = user.providerData.map((p) => p.providerId);
		const hasPasswordAuth = authProviders.includes('password');
		const hasGoogleAuth = authProviders.includes('google.com');
		const hasAppleAuth = authProviders.includes('apple.com');

		// Determine the primary provider (first in the list)
		const primaryProvider = user.providerData[0]?.providerId || 'unknown';
		// Keep loginType for backward compatibility but it will be consistent
		const loginProvider = primaryProvider;

		// Check for Apple-specific provider data
		const appleInfo = user.providerData.find(
			(p) => p.providerId === 'apple.com'
		);
		// Basic user data that we'll always save/update
		const userData = {
			uid: user.uid,
			displayName: user.displayName || '',
			email: user.email || '',
			joinedSince: new Date().toLocaleString('en-US', {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				second: '2-digit',
				hour12: true,
				timeZoneName: 'shortOffset',
			}),
			lastLogin: new Date().toISOString(),
			lastShownQuoteDate: new Date().toISOString().split('T')[0],
			loginType: loginProvider, // Keep for backward compatibility
			authProviders: authProviders, // NEW: Track all linked providers
			primaryProvider: primaryProvider, // NEW: Track primary provider
			hasPasswordAuth: hasPasswordAuth, // NEW: Quick check flags
			hasGoogleAuth: hasGoogleAuth,
			hasAppleAuth: hasAppleAuth,
			name: user.displayName || '',
			photoURL: user.photoURL || '',
			profileUrl: user.photoURL || '',
			profile: user.photoURL || '', // Add for mobile compatibility
			showQuotes: true,
			aivaUseCase: ['Work', 'School', 'Home'],
			defaultView: 'Daily',
			userTimezoneOffset: userTimezoneOffset,
		};
		if (appleInfo) {
			userData.appleUserId = appleInfo.uid;
			userData.identifier = appleInfo.uid;
		}
		// Check if user document already exists
		const userDoc = await getDoc(userRef);
		let isNewUser = false;
		if (!userDoc.exists()) {
			isNewUser = true;
			await setDoc(userRef, userData);
			// Create notification settings for new user with userId as document ID
			const notificationSettingsRef = doc(
				db,
				'notificationsSettings',
				user.uid
			);
			await setDoc(notificationSettingsRef, {
				dailyReviewEnabled: true,
				dailyReviewEveningTime: '08 : 00 pm',
				dailyReviewMorningTime: '08 : 00 am',
				habitRemainderEnabled: false,
				userId: user.uid,
				userTimezone: userTimezone,
				userTimezoneOffset: userTimezoneOffset,
				weeklyReviewDay: 'Sunday',
				weeklyReviewEnabled: true,
				weeklyReviewTime: '10 : 00 am',
			});
		} else {
			// User exists - update only necessary fields but ALWAYS update provider info
			const updateData = {
				lastLogin: userData.lastLogin,
				loginType: userData.loginType, // Keep for backward compatibility
				authProviders: userData.authProviders, // ALWAYS update provider list
				primaryProvider: userData.primaryProvider,
				hasPasswordAuth: userData.hasPasswordAuth,
				hasGoogleAuth: userData.hasGoogleAuth,
				hasAppleAuth: userData.hasAppleAuth,
				userTimezoneOffset: userData.userTimezoneOffset,
			};

			// Only update display name if it exists
			if (user.displayName) {
				updateData.displayName = user.displayName;
				updateData.name = user.displayName;
			}

			// DON'T overwrite profileUrl if it already exists in Firestore
			// This preserves custom uploaded photos from mobile or web
			const existingData = userDoc.data();
			if (!existingData.profileUrl && !existingData.profile) {
				// Only set profileUrl if neither profileUrl nor profile exists
				if (user.photoURL) {
					updateData.photoURL = user.photoURL;
					updateData.profileUrl = user.photoURL;
					updateData.profile = user.photoURL;
				}
			}

			await setDoc(userRef, updateData, { merge: true });
		}

		// Make sure to include existing user data from Firestore
		const existingData = userDoc.exists() ? userDoc.data() : {};
		return { ...existingData, ...userData, isNewUser };
	} catch (error) {
		console.error('Error saving user to Firestore:', error);
		// Return basic user data even if Firestore save fails
		return {
			uid: user.uid,
			email: user.email,
			displayName: user.displayName,
			photoURL: user.photoURL,
		};
	}
};
